/**
 * Format and parse measurement values in the ruler unit (pica, point, inch, mm).
 * Canvas/storage is always in points (72 pt = 1 inch). Use these helpers for
 * display and input in X, Y, W, H, margins, gutters, etc.
 */

import type { Unit } from '@/lib/ruler/types/unit'

const PT_PER_INCH = 72
const PT_PER_PICA = 12
const MM_PER_INCH = 25.4

/**
 * Convert a value in points to the display value in the given unit.
 * Use for showing X, Y, W, H, margins, gutters in the UI.
 */
export function pointsToDisplayValue(points: number, unit: Unit): number {
  switch (unit) {
    case 'pica':
      return points / PT_PER_PICA
    case 'point':
      return points
    case 'inch':
      return points / PT_PER_INCH
    case 'mm':
      return (points / PT_PER_INCH) * MM_PER_INCH
  }
}

/**
 * Format a point value as a string for display in the given unit.
 * Matches ruler tick formatting: pica rounded, point integer, inch 2 decimals, mm rounded.
 */
export function formatPointsAsUnit(points: number, unit: Unit): string {
  const v = pointsToDisplayValue(points, unit)

  switch (unit) {
    case 'pica':
      return Math.round(v).toString()
    case 'point':
      return Math.round(v).toString()
    case 'inch':
      return v.toFixed(2)
    case 'mm':
      return Math.round(v).toString()
  }
}

/**
 * Short suffix for the unit (for labels next to inputs).
 */
export function getUnitSuffix(unit: Unit): string {
  switch (unit) {
    case 'pica':
      return 'p0'
    case 'point':
      return 'pt'
    case 'inch':
      return 'in'
    case 'mm':
      return 'mm'
  }
}
